import { Modal } from "antd";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Input from "../../../components/input";
import Button from "../../../components/button";
import useRequest from "../../../components/hooks/use-request";
import { showToast } from "../../../components/toast";
import { CircleLoader } from "react-spinners";
import Select from "../../../components/select";
import Icon from "../../../assets/icons";

interface UserModalProp {
  isVisible: boolean;
  onClose: () => void;
  userData: any
}

interface UserName {
  username: string;
}
interface Permission {
  resource: string;
  actions: string[];
  _id: string;
}

interface RolesData {
  _id: string;
  createdAt: string;
  updatedAt: string;
  permissions: Permission[];
  name: string;
  isActive?: string;
  [key: string]: string | undefined | Permission[];
}

const EditUser: React.FC<UserModalProp> = ({ isVisible, onClose, userData }) => {
  const [deleteModal, setDeleteModal] = useState(false);
  const [user, setUser] = useState<UserName | null>(null);
  const { loading, makeRequest } = useRequest(`/users/${userData?._id}`, "PUT");
  const { makeRequest: singleUser } = useRequest(`/users/${userData?._id}`, "GET");
  const [roles, setRoles] = useState<RolesData[]>([]);
  const { loading: deleteLoading, makeRequest: deleteUser } = useRequest(
    `/users/${userData?._id}`,
    "DELETE"
  );

  const { makeRequest: getRoles } = useRequest("/roles", "GET");
  const { handleSubmit, control, reset } = useForm();

  const handleEditUser = handleSubmit(async (formData) => {
    const inviteUser = {
      username: formData.username,
      email: formData.email,
      role: formData.role,
    };
    const [response] = await makeRequest(inviteUser);
    if (response.status) {
      showToast(response.message, true, {
        position: "top-right",
      });
      reset();
      onClose();
      window.location.reload();
    } else {
      showToast(response?.message, false, {
        position: "top-right",
      });
    }
  });

  useEffect(() => {
    const fetchSingleUser = async () => {
      if (deleteModal) {
        const [response] = await singleUser();
        if (response.status) {
          
          setUser(response.data?.user);
        }
      }
    };
    fetchSingleUser();
  }, [deleteModal]);

  async function handleDeleteUser() {
    const [response] = await deleteUser();
    if (response.status) {
      showToast(response.message, true, {
        position: "top-right",
      });
      reset();
      window.location.reload();
    } else {
      showToast(response.message, false, {
        position: "top-right",
      });
    }
  }

  function closeDeleteUserModal() {
    setDeleteModal(false);
  }

  function openDeleteUserModal() {
    setDeleteModal(true);
  }


  async function fetchRolesData() {
    const [response] = await getRoles(undefined, {});
    setRoles(response?.data?.roles || []);
  }
  
  useEffect(() => {
    fetchRolesData();
  }, []);

  return (
    <>
      <Modal
        visible={isVisible}
        onCancel={onClose}
        width={560}
        footer={null}
        style={{
          position: "absolute",
          top: "10px",
          right: "20px",
          margin: 0,
          paddingBottom: 0,
        }}

      >
        <h2 className="text-[#272728] text-[30px] font-semibold">Edit User</h2>
        <form onSubmit={handleEditUser}>
          <div className="grid gap-4 mt-5">
           
            <Controller
              name="email"
              control={control}
              defaultValue={userData?.email}
              rules={{
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Enter a valid email.",
                },
              }}
              render={({ field, fieldState }) => (
                <Input
                  label="Email"
                  value={field.value}
                  placeholder="Enter Email"
                  className="w-full "
                  error={fieldState?.error?.message}
                  onChange={field.onChange}
                  autoComplete="off"
                />
              )}
            />{" "}
            <Controller
              name="username"
              control={control}
              defaultValue={userData?.username}
              rules={{
                required: "Full Name is required",
              }}
              render={({ field, fieldState }) => (
                <Input
                  value={field.value}
                  className="w-full"
                  label="Username"
                  placeholder="Enter Full Name"
                  error={fieldState?.error?.message}
                  onChange={field.onChange}
                  autoComplete="off"
                />
              )}
            />
            <Controller
              name="role"
              control={control}
              defaultValue={userData?.role}
              render={({ field }) => (
                <Select
                  name="role"
                  options={roles.map((role: any) => ({
                    value: role.name, 
                    label: role.name, 
                  }))}
                  className=" w-full "
                  label="Role"
                  onChange={(selectedValue) => {
                    field.onChange(selectedValue);
                  }}
                  value={field.value}
                />
              )}
            />
            {/* <Controller
            name="status"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Select
                name="status"
                options={[
                  { value: "active", label: "Active" },
                  { value: "inactive", label: "Inactive" },
                ]}
                className=" w-full "
                label="Role"
                onChange={(selectedValue) => {
                  field.onChange(selectedValue);
                }}
                value={field.value}
              />
            )}
          /> */}
          </div>
          <div className="flex flex-col justify-end gap-3 pt-14">
            <Button
              size="lg"
              variant="tertiary"
              type="button"
              className="font-normal"
              onClick={openDeleteUserModal}
            >
              {deleteLoading ? (
                <CircleLoader
                  color="#ffffff"
                  loading={deleteLoading}
                  size={20}
                />
              ) : (
                <div className="flex gap-3">
                  <Icon name="deleteIcon" /> Delete User
                </div>
              )}
            </Button>

            <Button
              size="lg"
              variant="primary"
              type="submit"
              className="font-normal"
            >
              {loading ? (
                <CircleLoader color="#ffffff" loading={loading} size={20} />
              ) : (
                <div className="flex gap-3">
                  <Icon name="editIcon" /> Edit User
                </div>
              )}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        visible={deleteModal}
        onCancel={closeDeleteUserModal}
        width={560}
        footer={null}
        closable={true}
        closeIcon={<span className="text-gray-500 text-lg">&times;</span>}
      >
        <div className="py-4">
          <div className="flex gap-4 items-start mb-4">
            <Icon name="warningIcon" />
            <div>
              <h2 className=" font-semibold text-[#1A1A1A] text-[16px] ">
                Delete User
              </h2>
              <p className="text-[#4E4E4E] text-[14px]">
                Are you sure you want to delete User "{user?.username}"
              </p>
            </div>
          </div>

          <div className="flex justify-start gap-4 mt-6">
            <button
              className="bg-[#D97706] w-[117px] h-[40px] hover:bg-amber-600 rounded-full text-white"
              onClick={handleDeleteUser}
            >
              Yes, Continue
            </button>
            <button
              className="bg-white w-[117px] h-[40px] text-[#4E4E4E] rounded-full border-2 font-medium border-[#9D9898]"
              onClick={closeDeleteUserModal}
            >
              No, Go back
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default EditUser;